import { useTelemetryStore } from '@/state/telemetryStore'
import { useUiStore } from '@/state/uiStore'
import type { SensorPlacement } from '@/features/ble/types'

const PLACEMENT_LABELS: Record<SensorPlacement, string> = {
  leftArm: 'Left Arm',
  rightArm: 'Right Arm',
  leftHand: 'Left Hand',
  rightHand: 'Right Hand',
}

function formatVector(value: { x: number; y: number; z: number }): string {
  return `(${value.x.toFixed(2)}, ${value.y.toFixed(2)}, ${value.z.toFixed(2)})`
}

function formatAngle(value: number): string {
  return `${value.toFixed(1)}°`
}

/**
 * SensorInfoPanel shows live properties for the currently selected sensor.
 * Rendered as an overlay on the right side of the 3D viewport.
 */
export function SensorInfoPanel() {
  const selectedSensor = useUiStore((state) => state.selectedSensor)
  const telemetry = useTelemetryStore((state) =>
    selectedSensor ? state.telemetry[selectedSensor] : undefined,
  )

  if (!selectedSensor || !telemetry) {
    return null
  }

  return (
    <aside className="pointer-events-none absolute right-4 top-4 w-64 rounded-[1.5rem] border border-edge/60 bg-surface/95 p-4 text-primary shadow-panel">
      <div className="mb-3 flex items-center justify-between gap-2 border-b border-edge/60 pb-2.5">
        <span className="eyebrow text-accent">Sensor</span>
        <span className="font-mono text-[11px] text-primary">
          {PLACEMENT_LABELS[selectedSensor]}
        </span>
      </div>

      <dl className="space-y-3">
        <div>
          <dt className="eyebrow">Angle to previous link</dt>
          <dd className="mt-1 font-mono text-sm tabular-nums text-primary">
            {formatAngle(telemetry.angleToPreviousLinkDeg)}
          </dd>
        </div>

        <div>
          <dt className="eyebrow">Angle to horizontal plane</dt>
          <dd className="mt-1 font-mono text-sm tabular-nums text-primary">
            {formatAngle(telemetry.angleToHorizontalDeg)}
          </dd>
        </div>

        <div>
          <dt className="eyebrow">Position</dt>
          <dd className="mt-1 font-mono text-sm tabular-nums text-primary">
            {formatVector(telemetry.position)}
          </dd>
        </div>

        <div>
          <dt className="eyebrow">Orientation (roll · pitch · yaw)</dt>
          <dd className="mt-1 font-mono text-sm tabular-nums text-primary">
            {formatAngle(telemetry.rollPitchYaw.roll)} · {formatAngle(telemetry.rollPitchYaw.pitch)}{' '}
            · {formatAngle(telemetry.rollPitchYaw.yaw)}
          </dd>
        </div>
      </dl>
    </aside>
  )
}
